import {
	Grid,
	InputAdornment,
	TextField,
	Typography,
	styled,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';

import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import Breadcrumbs from 'common/components/Breadcrumbs/Breadcrumbs';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import StepperComponent from 'common/components/Stepper/Stepper';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { ClusterReview } from 'common/constants/TextContent';
import {
	dateInCorrectFormatWithFullYear,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import { getFeedingInventoryData } from '../api';

import { ReactComponent as RefreshIcon } from '../../../common/assets/images/refresh.svg';
import PageTitle from '../../../components/PageTitle/PageTitle';

import ClusterTable from './ClusterTable/ClusterTable';

import styles from './ClusterreviewLanding.module.scss';

const { searchFieldBackground, searchFieldBorderRadius, fillColor } = styles;

const steps = [
	'Cluster Set Assignment',
	'Calibrate Control Set',
	'Profile Creation',
	'QA & Approval',
	'Cluster Review',
];

const { aimodels, controlDeDuplication } = rrcpDashboardRoutes;

export const StyledTextField = styled(TextField)({
	background: searchFieldBackground,
	borderRadius: searchFieldBorderRadius,
	width: '70%',
});

const ClusterReviewlayout = () => {
	const location = useLocation();
	const {
		state: { clusterName },
	} = location;
	const stepperIndex = 0;
	const [lastExecuted, setLastExecuted] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);

	const getRunClusterResponseData = async () => {
		setLoading(true);
		const response = await getFeedingInventoryData();
		if (response) {
			if (response?.responseBody) {
				const responseData = response?.responseBody;
				setLastExecuted(responseData?.lastExecutionTime);
				setLoading(false);
			}
		}
		setLoading(false);
	};

	useEffect(() => {
		getRunClusterResponseData();
	}, []);

	ClusterReview.title = clusterName;
	ClusterReview.body = (
		<div>
			{lastExecuted && (
				<div>
					Last Executed:
					<span className={styles['date-style']}>
						{lastExecuted
							? ` ${dateInCorrectFormatWithFullYear(lastExecuted)}`
							: '--/--/--'}
					</span>
				</div>
			)}
		</div>
	);

	const dateFormate = (lastExecutionEndTime: any) => {
		const parts = lastExecutionEndTime.split('/');
		const month = parseInt(parts[0], 10);
		const day = parseInt(parts[1], 10);
		const year = 2000 + parseInt(parts[2], 10);
		const date = new Date(year, month - 1, day);
		const options: any = { year: 'numeric', month: 'long', day: 'numeric' };
		return date.toLocaleDateString(undefined, options);
	};

	const secondLevelBreadcrumbs = [
		<Link key="1" to={urlWithTenant(aimodels)} data-testid="secondLevel-test">
			AI Models
		</Link>,
		<Link
			key="2"
			state={{ clusterName }}
			to={urlWithTenant(`/${aimodels}/${controlDeDuplication}`)}
		>
			Control De-duplication
		</Link>,
		<Typography key="2">{ClusterReview.title}</Typography>,
	];

	return (
		<Grid xs={12}>
			<Grid item>
				<Breadcrumbs breadcrumbs={secondLevelBreadcrumbs} />
				<PageTitle text={ClusterReview} />
			</Grid>
			{!loading ? (
				<Grid>
					<Grid item className={`${styles['main-content']}`}>
						<StepperComponent steps={steps} activeIndex={stepperIndex} />
					</Grid>
					<Grid item>
						<Grid className={`${styles['main-content']}`}>
							<Grid
								container
								alignItems="center"
								className={styles['table-des']}
								spacing={1}
							>
								<Grid item xs={4} md={8} lg={6} xl={8}>
									<StyledTextField
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchOutlined
														className={styles['input-search-icon']}
													/>
												</InputAdornment>
											),
										}}
										id="fullWidth"
										placeholder="Search"
										data-testid="searchInput"
										disabled
									/>
								</Grid>
								{!lastExecuted && (
									<>
										<Grid item xs={4} md={2} lg={3} xl={2}>
											<ButtonComp btnType="text">
												<RefreshIcon
													fill={fillColor}
													className={styles['refresh-icon']}
												/>
												Refresh Data
											</ButtonComp>
										</Grid>
										<Grid item xs={4} md={2} lg={3} xl={2}>
											<ButtonComp btnType="tertiary" disabled>
												Invalid Cluster
											</ButtonComp>
										</Grid>
									</>
								)}
							</Grid>
							{lastExecuted && (
								<Grid
									container
									alignItems="center"
									className={styles['table-des']}
									spacing={1}
								>
									<Grid item xs={4} md={6} lg={8} xl={8}>
										<Typography
											className={styles['table-desc-text']}
										>{`Displayed results as of ${dateFormate(
											dateInCorrectFormatWithFullYear(lastExecuted),
										)}. Kindly refresh for the most recent data.`}</Typography>
									</Grid>
									<Grid item xs={4} md={3} lg={2} xl={2}>
										<ButtonComp btnType="text">
											<RefreshIcon
												fill={fillColor}
												className={styles['refresh-icon']}
											/>
											Refresh Data
										</ButtonComp>
									</Grid>
									<Grid item xs={4} md={3} lg={2} xl={2}>
										<ButtonComp btnType="tertiary" disabled>
											Invalid Cluster
										</ButtonComp>
									</Grid>
								</Grid>
							)}
							<Grid item className={styles['cluster-table']}>
								<ClusterTable />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			) : (
				<Loader show />
			)}
		</Grid>
	);
};

export default ClusterReviewlayout;
